import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Title,
  Text,
  Card,
  Badge,
  Button,
  TextInput,
  NumberInput,
} from '@tremor/react';
import { TestTube2, PlusCircle, Save, Trash2 } from 'lucide-react';
import { LoadingSkeleton } from '@/components/LoadingSkeleton';
import { api, extractErrorMessage } from '@/lib/api';

interface Variant {
  id: string;
  label: string;
  weight: number;
}

interface Experiment {
  key: string;
  description: string | null;
  type: 'flat' | 'variants';
  raw_value: string;
  variants: Variant[];
  updated_at: string | null;
  updated_by: string | null;
}

function colorForVariant(v: string): 'blue' | 'green' | 'amber' | 'red' | 'slate' {
  if (v === 'control') return 'slate';
  if (v === 'a' || v === 'v1') return 'blue';
  if (v === 'b' || v === 'v2') return 'green';
  if (v === 'c' || v === 'v3') return 'amber';
  return 'blue';
}

interface DraftState {
  description: string;
  variants: Variant[];
}

export function ExperimentsPage() {
  const qc = useQueryClient();
  const [drafts, setDrafts] = useState<Record<string, DraftState>>({});
  const [error, setError] = useState<string | null>(null);
  const [newKey, setNewKey] = useState<string>('');

  const exps = useQuery<Experiment[]>({
    queryKey: ['experiments.list'],
    queryFn: async () => (await api().get('/experiments')).data,
    refetchInterval: 60_000,
  });

  const upsert = useMutation({
    mutationFn: async (vars: { key: string; body: DraftState }) => {
      const r = await api().put(`/experiments/${encodeURIComponent(vars.key)}`, {
        description: vars.body.description,
        variants: vars.body.variants,
      });
      return r.data;
    },
    onSuccess: (_data, vars) => {
      setError(null);
      setDrafts((d) => {
        const next = { ...d };
        delete next[vars.key];
        return next;
      });
      qc.invalidateQueries({ queryKey: ['experiments.list'] });
    },
    onError: (err) => setError(extractErrorMessage(err, 'Falha ao salvar')),
  });

  if (exps.isLoading) return <LoadingSkeleton />;

  const ensureDraft = (e: Experiment): DraftState =>
    drafts[e.key] ?? {
      description: e.description ?? '',
      variants:
        e.type === 'variants'
          ? e.variants.map((v) => ({ ...v }))
          : [
              { id: 'control', label: 'Control', weight: 100 },
              { id: 'b', label: 'Variant B', weight: 0 },
            ],
    };

  const updateDraft = (key: string, fn: (d: DraftState) => DraftState) =>
    setDrafts((all) => {
      const current =
        all[key] ?? ensureDraft(exps.data?.find((e) => e.key === key) as Experiment);
      return { ...all, [key]: fn(current) };
    });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Title>Experiments</Title>
          <Text>
            Variantes A/B persistidas em <code>system_setting</code> com
            prefixo <code>exp:</code>. Total deve somar 100%.
          </Text>
        </div>
        <Badge color="slate" icon={TestTube2}>
          {(exps.data ?? []).length} experimentos
        </Badge>
      </div>

      {error && (
        <Card className="border border-red-700/40 bg-red-950/20">
          <div className="text-sm text-red-300">{error}</div>
        </Card>
      )}

      {/* New experiment */}
      <Card>
        <Title>Criar experimento</Title>
        <Text>
          Chave deve começar com <code>exp:</code>. Será criada com 2 variantes
          (control 100% / B 0%).
        </Text>
        <div className="mt-3 flex gap-2">
          <TextInput
            placeholder="exp:nova_landing"
            value={newKey}
            onChange={(e) => setNewKey(e.target.value)}
          />
          <Button
            icon={PlusCircle}
            disabled={!newKey || !newKey.startsWith('exp:')}
            onClick={() =>
              upsert.mutate({
                key: newKey,
                body: {
                  description: 'Novo experimento',
                  variants: [
                    { id: 'control', label: 'Control', weight: 100 },
                    { id: 'b', label: 'Variant B', weight: 0 },
                  ],
                },
              })
            }
          >
            Criar
          </Button>
        </div>
      </Card>

      {/* List */}
      {(exps.data ?? []).length === 0 ? (
        <Card className="border border-dashed border-slate-700">
          <Text className="text-slate-400">
            Nenhum experimento ainda. Crie o primeiro acima.
          </Text>
        </Card>
      ) : (
        (exps.data ?? []).map((e) => {
          const draft = ensureDraft(e);
          const total = draft.variants.reduce((acc, v) => acc + v.weight, 0);
          const dirty =
            JSON.stringify(draft) !==
            JSON.stringify({
              description: e.description ?? '',
              variants:
                e.type === 'variants' ? e.variants : draft.variants,
            });
          return (
            <Card key={e.key}>
              <div className="flex items-center justify-between">
                <div>
                  <code className="text-blue-300">{e.key}</code>
                  <div className="text-xs text-slate-500">
                    {e.updated_at &&
                      `atualizado ${new Date(e.updated_at).toLocaleString('pt-BR')}${
                        e.updated_by ? ` por ${e.updated_by}` : ''
                      }`}
                  </div>
                </div>
                <Badge color={total === 100 ? 'green' : 'red'} size="xs">
                  total {total}%
                </Badge>
              </div>

              <div className="mt-3 space-y-2">
                <TextInput
                  placeholder="descrição"
                  value={draft.description}
                  onChange={(ev) =>
                    updateDraft(e.key, (d) => ({
                      ...d,
                      description: ev.target.value,
                    }))
                  }
                />

                {draft.variants.map((v, idx) => (
                  <div
                    key={`${e.key}-${idx}`}
                    className="grid grid-cols-12 items-center gap-2"
                  >
                    <div className="col-span-3 text-xs">
                      <Badge color={colorForVariant(v.id)} size="xs">
                        {v.id}
                      </Badge>
                    </div>
                    <TextInput
                      className="col-span-5"
                      value={v.label}
                      onChange={(ev) =>
                        updateDraft(e.key, (d) => {
                          const variants = [...d.variants];
                          variants[idx] = { ...variants[idx], label: ev.target.value };
                          return { ...d, variants };
                        })
                      }
                    />
                    <NumberInput
                      className="col-span-3"
                      value={v.weight}
                      min={0}
                      max={100}
                      onValueChange={(val) =>
                        updateDraft(e.key, (d) => {
                          const variants = [...d.variants];
                          variants[idx] = {
                            ...variants[idx],
                            weight: Number(val ?? 0),
                          };
                          return { ...d, variants };
                        })
                      }
                    />
                    <button
                      className="col-span-1 text-red-400 hover:text-red-300"
                      onClick={() =>
                        updateDraft(e.key, (d) => ({
                          ...d,
                          variants: d.variants.filter((_, i) => i !== idx),
                        }))
                      }
                      type="button"
                      title="remove"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}

                <div className="flex justify-between gap-2 pt-2">
                  <Button
                    size="xs"
                    variant="secondary"
                    icon={PlusCircle}
                    onClick={() =>
                      updateDraft(e.key, (d) => ({
                        ...d,
                        variants: [
                          ...d.variants,
                          {
                            id: `v${d.variants.length + 1}`,
                            label: 'New variant',
                            weight: 0,
                          },
                        ],
                      }))
                    }
                  >
                    Add variant
                  </Button>
                  <Button
                    icon={Save}
                    disabled={!dirty || total !== 100}
                    onClick={() =>
                      upsert.mutate({ key: e.key, body: draft })
                    }
                  >
                    Salvar
                  </Button>
                </div>
              </div>
            </Card>
          );
        })
      )}
    </div>
  );
}
